 /**
  * Platform detection utilities for Capacitor native apps
  */
 
declare global {
  interface Window {
    Capacitor?: {
      isNativePlatform: () => boolean;
      getPlatform: () => 'web' | 'android' | 'ios';
      // Newer Capacitor versions expose appId here. Older versions don't, so optional.
      AppInfo?: { appId?: string };
    };
  }
}

/**
 * Returns the deep-link scheme to use for OAuth callbacks based on which
 * Capacitor app is running. Defaults to "gyansadhan".
 *
 * Detection order:
 *  1. Build-time env var VITE_APP_SCHEME (if set per-app build)
 *  2. Capacitor appId mapping (com.edulyt.app -> "edulyt")
 *  3. Fallback "gyansadhan"
 */
export function getAppScheme(): 'gyansadhan' | 'edulyt' {
  const envScheme = (import.meta as any).env?.VITE_APP_SCHEME;
  if (envScheme === 'edulyt' || envScheme === 'gyansadhan') return envScheme;

  const appId = (typeof window !== 'undefined' && window.Capacitor?.AppInfo?.appId) || '';
  if (appId === 'com.edulyt.app') return 'edulyt';
  return 'gyansadhan';
}
 
 /**
  * Check if running inside a Capacitor native app (Android or iOS)
  */
 export function isCapacitorNative(): boolean {
   return (
     typeof window !== 'undefined' &&
     typeof window.Capacitor !== 'undefined' &&
     window.Capacitor.isNativePlatform()
   );
 }
 
 /**
  * Check if running on Android
  */
 export function isAndroid(): boolean {
   return isCapacitorNative() && window.Capacitor?.getPlatform() === 'android';
 }
 
 /**
  * Check if running on iOS
  */
 export function isIOS(): boolean {
   return isCapacitorNative() && window.Capacitor?.getPlatform() === 'ios';
 }
 
 /**
  * Get current platform
  */
 export function getPlatform(): 'web' | 'android' | 'ios' {
   if (!isCapacitorNative()) return 'web';
   return window.Capacitor?.getPlatform() || 'web';
 }